import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  COURSE_CONTENT_NAME,
  COURSE_LIST_CURRENCY,
  COURSE_LIST_VALUE,
} from "../metaCourse";
import { COURSE_PRICE_ID } from "../publicProgram";
import { metaPixelService } from "../services/metaPixelService";
import { hasSensitiveSearch } from "../utils/analyticsUrl";
import {
  CONSENT_EVENT,
  CONSENT_KEY,
  type CookiePrefs,
  readMarketingConsent,
} from "../utils/cookieConsent";
import { rememberFbclid } from "../utils/metaClick";

const viewContent = {
  content_name: COURSE_CONTENT_NAME,
  content_ids: [COURSE_PRICE_ID],
  content_type: "product",
  value: COURSE_LIST_VALUE,
  currency: COURSE_LIST_CURRENCY,
};

/** Loads the Meta pixel after marketing consent and records page and landing views. */
export default function MetaRouteTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const [marketing, setMarketing] = useState(readMarketingConsent);

  useEffect(() => {
    const apply = () => setMarketing(readMarketingConsent());

    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<CookiePrefs>).detail;
      if (detail && typeof detail.marketing === "boolean") {
        setMarketing(detail.marketing);
        return;
      }
      apply();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === CONSENT_KEY) apply();
    };

    apply();
    window.addEventListener(CONSENT_EVENT, onConsent);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onConsent);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    rememberFbclid(location.search);
  }, [location.search]);

  useEffect(() => {
    if (!marketing) {
      metaPixelService.disable();
      return;
    }

    metaPixelService.enable();
    metaPixelService.setUser(user?.email ?? null, user?.uid ?? null);
    if (hasSensitiveSearch(location.search)) return;

    metaPixelService.track("PageView");
    if (location.pathname === "/") {
      metaPixelService.track("ViewContent", viewContent);
    }
  }, [marketing, location.pathname, location.search, user?.email, user?.uid]);

  return null;
}
